/**
 * Luật thuần của hạn mức token AI theo tháng (ADR-0088). Không dựng chuỗi hiển thị, không chọn màu:
 * view tự dịch, bảng tông ở `constants.ts`.
 */
import type { AiTokenQuota } from '../types.ts';

/** Tài nguyên của dòng hạn mức token AI trong `TenantDetail.quota[]`. */
export const TAI_NGUYEN_TOKEN_AI = 'AI_TOKENS_MONTH';

/** Trần của `limit_override` và `limit_value` theo OpenAPI (`maximum: 1000000000000`). */
export const TOKEN_TOI_DA = 1_000_000_000_000;

/** Từ mức này Tenant Admin được cảnh báo (ADR-0088): 80% và 100%. */
export const NGUONG_CANH_BAO = 80;

/**
 * Mức của một tenant trong tháng:
 * - `khong-gioi-han`: hạn mức hiệu lực `null`, không bao giờ chặn;
 * - `da-chan`: `blocked` của server — lời gọi AI mới bị từ chối;
 * - `canh-bao`: từ 80% trở lên mà chưa chặn;
 * - `binh-thuong`: còn lại.
 *
 * `blocked` đọc từ server, không tự so `used >= limit_value`: server là nơi quyết chặn.
 */
export type MucToken = 'khong-gioi-han' | 'binh-thuong' | 'canh-bao' | 'da-chan';

export function mucToken(q: Pick<AiTokenQuota, 'limit_value' | 'used_percent' | 'blocked'>): MucToken {
  if (q.blocked) return 'da-chan';
  if (q.limit_value === null) return 'khong-gioi-han';
  return (q.used_percent ?? 0) >= NGUONG_CANH_BAO ? 'canh-bao' : 'binh-thuong';
}

/**
 * Phần trăm đã dùng như server tính (`used_percent`): làm tròn xuống, hạn mức 0 thì 100. Dùng cho
 * dòng `AI_TOKENS_MONTH` của bảng hạn mức, nơi API chỉ trả `used` và `limit_value`.
 */
export function phanTramDaDung(used: number, limit: number): number {
  if (limit <= 0) return 100;
  return Math.floor((used * 100) / limit);
}

/** Bề rộng thanh tiến độ, kẹp 0–100: dùng vượt hạn mức (ghi trễ của module) không làm thanh tràn khung. */
export function beRongThanh(usedPercent: number | null): number {
  if (usedPercent === null || !Number.isFinite(usedPercent)) return 0;
  return Math.min(100, Math.max(0, usedPercent));
}

/**
 * Phần trăm để hiện: vượt 100 thì hiện `>100%` (`vuot`), vì "1.227%" khó đọc và không giúp gì thêm. Số đúng nằm ở
 * số token vượt (`vuotHanMuc`) đặt ngay cạnh.
 */
export function hienPhanTram(usedPercent: number | null): { so: number; vuot: boolean } {
  const pct = usedPercent !== null && Number.isFinite(usedPercent) ? Math.max(0, usedPercent) : 0;
  return pct > 100 ? { so: 100, vuot: true } : { so: pct, vuot: false };
}

/** Số token đã dùng quá hạn mức (ghi trễ, hạn mức bị hạ giữa tháng); `0` khi chưa vượt hoặc không giới hạn. */
export function vuotHanMuc(used: number, limit: number | null): number {
  return limit === null ? 0 : Math.max(0, used - limit);
}

/**
 * Hạn mức tháng (trước token cấp thêm) đến từ đâu (ADR-0091 mục 1): hạn mức đặt riêng cho tenant, không thì gói tài
 * nguyên của tenant. Token cấp thêm cộng **trên**, không đổi nguồn.
 */
export type NguonHanMuc = 'ghi-de' | 'goi-tai-nguyen' | 'khong-gioi-han';

export function nguonHanMuc(
  q: Pick<AiTokenQuota, 'limit_override'> & Partial<Pick<AiTokenQuota, 'resource_package' | 'plan_limit'>>,
): NguonHanMuc {
  if (q.limit_override !== null) return 'ghi-de';
  if (q.resource_package || (q.plan_limit !== null && q.plan_limit !== undefined)) return 'goi-tai-nguyen';
  return 'khong-gioi-han';
}

/** Kết quả đọc ô nhập số token. */
export type KetQuaSoToken =
  | { ok: true; so: number }
  | { ok: false; loi: 'trong' | 'sai' | 'qua-lon' };

/**
 * Đọc số token người dùng gõ. Nhận dấu phân nhóm của cả hai ngôn ngữ (`30.000.000`, `30,000,000`) và
 * khoảng trắng; không nhận số thập phân hay số âm — token là số nguyên không âm.
 */
export function docSoToken(nhap: string | number | null | undefined): KetQuaSoToken {
  if (nhap === null || nhap === undefined) return { ok: false, loi: 'trong' };
  if (typeof nhap === 'number') {
    if (!Number.isInteger(nhap) || nhap < 0) return { ok: false, loi: 'sai' };
    return nhap > TOKEN_TOI_DA ? { ok: false, loi: 'qua-lon' } : { ok: true, so: nhap };
  }
  const gon = nhap.trim();
  if (!gon) return { ok: false, loi: 'trong' };
  if (!/^\d{1,3}([.,\s]?\d{3})*$|^\d+$/.test(gon)) return { ok: false, loi: 'sai' };
  const so = Number(gon.replace(/[.,\s]/g, ''));
  if (!Number.isSafeInteger(so)) return { ok: false, loi: 'qua-lon' };
  return so > TOKEN_TOI_DA ? { ok: false, loi: 'qua-lon' } : { ok: true, so };
}

/** Hạn mức mới làm tenant bị chặn ngay trong tháng này (số đã dùng đã chạm hoặc vượt). */
export function seChanNgay(hanMucMoi: number | null, daDung: number): boolean {
  return hanMucMoi !== null && daDung >= hanMucMoi;
}

/**
 * Hạn mức tenant nhận khi **không** có hạn mức đặt riêng: token của gói tài nguyên, không thì `plan_limit` (token
 * của gói mặc định của gói nền); `null` là không giới hạn.
 */
export function hanMucTheoGoi(
  q: Pick<AiTokenQuota, 'plan_limit'> & Partial<Pick<AiTokenQuota, 'resource_package'>>,
): number | null {
  return q.resource_package ? q.resource_package.ai_tokens_month : q.plan_limit;
}

/**
 * Mã gói tài nguyên tenant đang dùng (ADR-0091 mục 1): gói đã gán, không thì gói mặc định của `plan_code` — gói mặc
 * định có mã trùng `plan_code`. Bộ lọc danh sách tenant và form sửa đều tính theo đây, như backend đếm `tenant_count`.
 */
export function maGoiTaiNguyenCua(t: { resource_package_code: string | null; plan_code: string }): string {
  return t.resource_package_code ?? t.plan_code;
}
